import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, animate, AnimatePresence } from "framer-motion";
import { Sparkles, Check, CircleDot, Trees, Flame, Send, ImagePlus, Settings2, ChevronDown, ChevronUp, Move, Type, Palette, Loader2, Copy, SlidersHorizontal, Download, X, ArrowRight, FolderPlus, MessageSquarePlus, Sun, Moon } from "lucide-react";

import { makePoster, makePosterWithImage, getThreads, addProject, getProjects, 
          addThread, getThreadRecords, renderPoster, directRender } from "../api/api";
import Cookies from 'js-cookie';

import config from "../utils/config";

import { ToastContainer, toast } from 'react-toastify';

// Custom Hooks
const useAnimatedValue = (value, duration = 0.5) => {
  const [animatedValue, setAnimatedValue] = useState(value);

  useEffect(() => {
    const controls = animate(animatedValue, value, {
      duration,
      onUpdate: (v) => setAnimatedValue(Math.round(parseFloat(v))),
    });
    return () => controls.stop();
  }, [value]);

  return animatedValue;
};

// --- Theme Utility ---
const getThemeClasses = (theme) => {
  const isDark = theme === 'dark';
  return {
    // Backgrounds & Base
    bgScreen: isDark ? 'bg-gray-900 text-white' : 'bg-white text-gray-900',
    bgHeader: isDark ? 'bg-gray-900/50 border-gray-700/50' : 'bg-white/90 border-gray-300/80',
    bgSidebar: isDark ? 'bg-gradient-to-br from-gray-800 to-gray-900 border-gray-700/50' : 'bg-gradient-to-br from-gray-50 to-gray-200 border-gray-300/80',
    bgMain: isDark ? 'bg-gradient-to-br from-gray-800/90 to-gray-900/90 border-gray-700/50' : 'bg-gradient-to-br from-white to-gray-50 border-gray-300/80',
    bgModal: isDark ? 'bg-gradient-to-br from-gray-800 to-gray-900 border-[#00ff9d]/30' : 'bg-white border-gray-300 shadow-xl',
    bgInput: isDark ? 'bg-gray-900/70 border-gray-700 focus:border-[#00ff9d]/50 text-white placeholder-white/40' : 'bg-white border-gray-300 focus:border-[#00ff9d]/50 text-gray-800 placeholder-gray-500',
    bgPromptBox: isDark ? 'bg-gray-800/80 border-gray-700/50' : 'bg-white/80 border-gray-300/50',
    bgToolbar: isDark ? 'bg-gradient-to-br from-gray-800/90 to-gray-900/90 border-gray-700/50' : 'bg-gradient-to-br from-gray-100 to-gray-200 border-gray-300/50',
    bgToolbarAction: isDark ? 'bg-gradient-to-t from-black/30 to-transparent border-gray-700/50' : 'bg-gradient-to-t from-gray-200/50 to-transparent border-gray-300/50',
    bgToolbarTab: isDark ? 'bg-gray-900/70 border-gray-700' : 'bg-gray-200 border-gray-300',

    // Text & Color
    textBase: isDark ? 'text-white' : 'text-gray-900',
    textSecondary: isDark ? 'text-white/60' : 'text-gray-600',
    textFaded: isDark ? 'text-white/40 italic' : 'text-gray-500 italic',
    textSidebarTitle: isDark ? 'text-white/70' : 'text-gray-700',
    textHeaderSub: isDark ? 'text-xs text-white/40' : 'text-xs text-gray-500',
    textPromptBoxDesc: isDark ? 'text-white/80' : 'text-gray-700',
    textPlaceholder: isDark ? 'placeholder-white/40' : 'placeholder-gray-500',
    textToolbarTitle: isDark ? 'text-white' : 'text-gray-900',
    textToolbarSub: isDark ? 'text-white/50' : 'text-gray-600',
    textInputLabel: isDark ? 'text-white/70' : 'text-gray-700',
    textWelcome: isDark ? 'text-white' : 'text-gray-900',
    textWelcomeSub: isDark ? 'text-white/60' : 'text-gray-600',
    
    // Sidebar Items
    bgSidebarItem: isDark ? 'bg-gray-700/50 hover:bg-gray-700/80 border border-transparent hover:border-[#00ff9d]/30' : 'bg-white hover:bg-gray-100 border border-gray-300 hover:border-[#00ff9d]/30',
    bgSidebarItemSelected: isDark ? 'bg-[#00ff9d]/20 border-[#00ff9d]/50 shadow-[0_0_15px_#00ff9d30]' : 'bg-[#00ff9d]/10 border-[#00ff9d]/30 shadow-md',
    textSidebarItemTitle: isDark ? 'text-white' : 'text-gray-900',
    textSidebarItemDesc: isDark ? 'text-white/50' : 'text-gray-600',

    // Image Card
    bgImageCard: isDark ? 'bg-gray-800/50 border-gray-700/50' : 'bg-white/80 border-gray-300/50',
    bgImagePrompt: isDark ? 'bg-gradient-to-r from-gray-800/50 to-gray-900/50 border-gray-700/50' : 'bg-white border-gray-300/50',
    textImagePrompt: isDark ? 'text-white/80' : 'text-gray-800',

    // Toolbar elements
    bgToolbarInput: isDark ? 'bg-gray-900/70 border-gray-700' : 'bg-white border-gray-300',
    bgToolbarRangeIndicator: isDark ? 'bg-gray-900/70 border-[#00ff9d]/20' : 'bg-white border-[#00ff9d]/30',
    textToolbarRangeIndicator: 'text-[#00ff9d]',
    
    // MiniLoader text
    textLoader: isDark ? 'text-white/60' : 'text-gray-600',
    
    // Other
    borderSidebar: isDark ? 'border-r border-gray-700/50' : 'border-r border-gray-300/80',
    borderMain: isDark ? 'border-b border-gray-700/50' : 'border-b border-gray-300/80',
    borderToolbarHeader: isDark ? 'border-b border-gray-700/50' : 'border-b border-gray-300/50',
    borderToolbarFooter: isDark ? 'border-t border-gray-700/50' : 'border-t border-gray-300/50',
    
    // Specific elements for Light Theme
    textAppLogo: isDark ? 'text-transparent' : 'text-gray-900',
  };
};

// Components
const MiniLoader = ({ theme }) => {
  const classes = getThemeClasses(theme);
  return (
    <motion.div
      className="flex flex-col items-center justify-center py-10 text-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      <div className="relative w-10 h-10 mb-4">
        <motion.div
          className="absolute inset-0 rounded-full border-4 border-[#00ff9d]/30"
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.6, 0.3, 0.6],
          }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className={`absolute inset-0 rounded-full border-4 border-t-[#00ff9d] border-r-[#22e4ac] border-l-transparent border-b-transparent`}
          animate={{ rotate: 360 }}
          transition={{ duration: 1.2, repeat: Infinity, ease: "linear" }}
        />
      </div>
      <motion.p
        className={`text-sm font-medium ${classes.textLoader}`}
        animate={{ opacity: [0.5, 1, 0.5] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
      >
        Loading...
      </motion.p>
    </motion.div>
  );
};

const Header = ({ theme, toggleTheme }) => {
  const classes = getThemeClasses(theme);
  return (
    <header className={`w-full flex items-center justify-between px-8 py-5 backdrop-blur-xl ${classes.bgHeader} sticky top-0 z-40`}>
      <div className="flex items-center gap-3">
        {/* White Logo (for dark theme) - Visible by default, hidden when the 'dark' class is on the root */}
        <img 
          src={`${theme === 'dark' ? '/dewaan/dewaan-w.png' : '/dewaan/dewaan-d.png'}`}
          alt="Dewaan Studio Logo" 
          className="h-20 dark:hidden"
        />
      </div>
      <div className="flex items-center gap-4">
        <div className={classes.textHeaderSub}></div>
        <button
          onClick={toggleTheme}
          className={`p-2 rounded-full transition-all ${theme === 'dark' ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
          title={`Switch to ${theme === 'dark' ? 'Light' : 'Dark'} Theme`}
        >
          {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
        </button>
      </div>
    </header>
  );
};

const SidebarSection = ({ title, items, onAdd, selectedId, onItemClick, isLoading, theme }) => {
  const classes = getThemeClasses(theme);
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className={`text-sm font-semibold ${classes.textSidebarTitle} uppercase tracking-wide flex items-center gap-2`}>
          {title === "Projects" ? <FolderPlus size={14} className={classes.textSidebarTitle} /> : <MessageSquarePlus size={14} className={classes.textSidebarTitle} />}
          {title}
        </h3>
        <button onClick={onAdd} className="text-[#00ff9d] hover:text-[#22e4ac] transition p-1.5 hover:bg-[#00ff9d]/10 rounded-lg">
          <ImagePlus size={16} />
        </button>
      </div>
      
      {isLoading ? (
        <div className="py-4">
          <MiniLoader theme={theme} />
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {items.length === 0 ? (
            <p className={`text-xs ${classes.textFaded}`}>No {title.toLowerCase()} yet</p>
          ) : (
            items.map((item) => (
              <div
                key={item._id}
                onClick={() => onItemClick(item._id)}
                className={`p-3 rounded-lg border transition cursor-pointer ${
                  selectedId === item._id
                    ? classes.bgSidebarItemSelected
                    : classes.bgSidebarItem
                }`}
              >
                <p className={`text-sm font-medium ${classes.textSidebarItemTitle}`}>{item.title}</p>
                {item.description && (
                  <p className={`text-xs ${classes.textSidebarItemDesc} mt-1 line-clamp-2`}>{item.description}</p>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const Sidebar = ({
  projects,
  threads,
  onProjectAdd,
  onThreadAdd,
  selectedProjectId,
  onProjectSelect,
  selectedThreadId,
  onThreadSelect,
  isProjectsLoading,
  isThreadsLoading,
  theme
}) => {
  const classes = getThemeClasses(theme);
  return (
    <aside className={`w-64 ${classes.borderSidebar} ${classes.bgSidebar} rounded-2xl shadow-2xl flex flex-col p-5 gap-6 overflow-y-auto shrink-0`}>
      <div className="mt-4">
        <SidebarSection
          title="Projects"
          items={projects}
          onAdd={onProjectAdd}
          selectedId={selectedProjectId}
          onItemClick={onProjectSelect}
          isLoading={isProjectsLoading}
          theme={theme}
        />
      </div>

      <SidebarSection
        title="Threads"
        items={threads}
        onAdd={onThreadAdd}
        selectedId={selectedThreadId}
        onItemClick={onThreadSelect}
        isLoading={isThreadsLoading}
        theme={theme}
      />

      <div className={`mt-auto text-xs ${classes.textSecondary}`}>
        <p>© 2025 Alienesia Labs</p>
      </div>
    </aside>
  );
};

//Generation Font Selector 
const FontSelector = ({ font, setFont, fontOptions, theme }) => {
  const [loadedFonts, setLoadedFonts] = useState(new Set());
  const classes = getThemeClasses(theme);

  // Load fonts dynamically
  useEffect(() => {
    Object.entries(fontOptions).forEach(([key, { path }]) => {
      if (!loadedFonts.has(key)) {
        const fontFace = new FontFace(key, `url(${path})`);
        fontFace.load().then((loaded) => {
          document.fonts.add(loaded);
          setLoadedFonts(prev => new Set([...prev, key]));
        }).catch(err => {
          console.warn(`Failed to load font ${key}:`, err);
        });
      }
    });
  }, [fontOptions, loadedFonts]);
  
  return (
    <div className="w-full">
      <label className={`text-sm ${classes.textInputLabel} mb-3 block flex items-center gap-2 font-medium`}>
        <span>🎨</span> Hit Font
      </label>
      
      {/* Horizontal scroll container with custom scrollbar */}
      <style jsx>{`
        .font-scroll::-webkit-scrollbar {
          height: 6px;
        }
        .font-scroll::-webkit-scrollbar-track {
          background: ${classes.textSecondary.includes('white') ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'};
          border-radius: 10px;
        }
        .font-scroll::-webkit-scrollbar-thumb {
          background: rgba(0, 255, 157, 0.3);
          border-radius: 10px;
        }
        .font-scroll::-webkit-scrollbar-thumb:hover {
          background: rgba(0, 255, 157, 0.5);
        }
      `}</style>
      
      <div className="relative">
        <div className="font-scroll flex gap-3 overflow-x-auto pb-3">
          {Object.entries(fontOptions).map(([key, { name }]) => (
            <motion.button
              key={key}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setFont(key)}
              className={`flex-shrink-0 px-4 py-2 transition-all min-w-[140px] border-b-2 ${
                font === key
                  ? 'border-[#00ff9d] bg-[#00ff9d]/5'
                  : theme === 'dark' ? 'border-gray-700 hover:border-gray-500 hover:bg-gray-800/30' : 'border-gray-300 hover:border-gray-500 hover:bg-gray-100/30'
              }`}>
              <div 
                className={`text-base ${classes.textBase}`}
                style={{ fontFamily: key }}>
                {name}
              </div>
            </motion.button>
          ))}
        </div>
      </div>
    </div>
  );
};

// Font Selector Component with dynamic font loading
const ToolbarFontSelector = ({ editFont, setEditFont, fontOptions, theme }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loadedFonts, setLoadedFonts] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = React.useRef(null);
  const dropdownRef = React.useRef(null);
  
  const getThemeClasses = (theme) => ({
    textInputLabel: theme === 'dark' ? 'text-white/70' : 'text-gray-700',
    bgToolbarInput: theme === 'dark' ? 'bg-gray-800/60' : 'bg-gray-100/80',
    textToolbarTitle: theme === 'dark' ? 'text-white/90' : 'text-gray-800',
  });
  
  const classes = getThemeClasses(theme);
  
  // Load fonts dynamically
  React.useEffect(() => {
    Object.entries(fontOptions).forEach(([key, { path }]) => {
      if (!loadedFonts.has(key)) {
        const fontFace = new FontFace(key, `url(${path})`);
        fontFace.load().then((loaded) => {
          document.fonts.add(loaded);
          setLoadedFonts(prev => new Set([...prev, key]));
        }).catch(err => {
          console.warn(`Failed to load font ${key}:`, err);
        });
      }
    });
  }, [fontOptions, loadedFonts]);

  // Update dropdown position when opening
  React.useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownHeight = 400;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      // Decide whether to show dropdown above or below
      const showAbove = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
      
      setDropdownPosition({
        top: showAbove ? rect.top - dropdownHeight - 8 : rect.bottom + 8,
        left: rect.left,
        width: rect.width,
        showAbove
      });
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        buttonRef.current && 
        !buttonRef.current.contains(event.target) &&
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Filter fonts based on search
  const filteredFonts = Object.entries(fontOptions).filter(([key, { name }]) =>
    name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  return (
    <div>
      <label className={`text-xs ${classes.textInputLabel} mb-2 block font-medium`}>
        Font Family
      </label>
      
      <div className="relative">
        <button
          ref={buttonRef}
          onClick={() => setIsOpen(!isOpen)}
          className={`w-full ${classes.bgToolbarInput} rounded-lg p-3 text-sm ${classes.textToolbarTitle} focus:ring-2 focus:ring-[#00ff9d]/50 outline-none hover:bg-opacity-80 transition flex items-center justify-between border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-300'}`}
        >
          <span style={{ fontFamily: editFont }} className="truncate">
            {fontOptions[editFont]?.name || editFont}
          </span>
          <motion.span
            animate={{ rotate: isOpen ? 180 : 0 }}
            transition={{ duration: 0.2 }}
            className="text-[#00ff9d] ml-2 shrink-0"
          >
            <ChevronDown size={16} />
          </motion.span>
        </button>
        
        {/* Render dropdown in a portal-like fixed position */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              ref={dropdownRef}
              initial={{ opacity: 0, scale: 0.95, y: dropdownPosition.showAbove ? 10 : -10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: dropdownPosition.showAbove ? 10 : -10 }}
              transition={{ duration: 0.15 }}
              className={`fixed ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'} border-2 ${theme === 'dark' ? 'border-gray-700' : 'border-gray-300'} rounded-xl overflow-hidden shadow-2xl flex flex-col`}
              style={{ 
                top: `${dropdownPosition.top}px`,
                left: `${dropdownPosition.left}px`,
                width: `${dropdownPosition.width}px`,
                maxHeight: '400px',
                zIndex: 9999
              }}
            >
              {/* Search Bar */}
              <div className={`p-3 border-b ${theme === 'dark' ? 'border-gray-700 bg-gray-800/50' : 'border-gray-300 bg-gray-50'} sticky top-0 z-10`}>
                <input
                  type="text"
                  placeholder="Search fonts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`w-full px-3 py-2 rounded-lg text-xs ${theme === 'dark' ? 'bg-gray-900 text-white border-gray-700' : 'bg-white text-gray-900 border-gray-300'} border outline-none focus:ring-2 focus:ring-[#00ff9d]/50 placeholder:text-gray-500`}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                />
              </div>

              {/* Font Options List */}
              <div className="overflow-y-auto" style={{ maxHeight: '320px' }}>
                {filteredFonts.length > 0 ? (
                  filteredFonts.map(([key, { name }]) => (
                    <button
                      key={key}
                      onClick={() => {
                        setEditFont(key);
                        setIsOpen(false);
                        setSearchTerm('');
                      }}
                      className={`w-full px-4 py-3.5 text-left hover:bg-[#00ff9d]/10 transition-all border-l-4 flex items-center justify-between ${
                        editFont === key 
                          ? 'border-[#00ff9d] bg-[#00ff9d]/10' 
                          : `border-transparent ${theme === 'dark' ? 'hover:bg-gray-800' : 'hover:bg-gray-50'}`
                      }`}
                      style={{ fontFamily: key }}
                    >
                      <div className="flex flex-col gap-1 flex-1 min-w-0">
                        <span className={`text-sm font-medium ${editFont === key ? 'text-[#00ff9d]' : classes.textToolbarTitle}`}>
                          {name}
                        </span>
                        <span 
                          className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'} truncate`}
                          style={{ fontFamily: key }}
                        >
                          The quick brown fox
                        </span>
                      </div>
                      {editFont === key && (
                        <span className="text-[#00ff9d] ml-2 shrink-0 font-bold">✓</span>
                      )}
                    </button>
                  ))
                ) : (
                  <div className={`px-4 py-8 text-center text-sm ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                    No fonts found
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className={`p-2 border-t ${theme === 'dark' ? 'border-gray-700 bg-gray-800/50' : 'border-gray-300 bg-gray-50'} text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'} text-center`}>
                {filteredFonts.length} font{filteredFonts.length !== 1 ? 's' : ''} available
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};


// Prompt Box Component
const PromptBox = ({
  prompt,
  onPromptChange,
  font,
  setFont,
  onGenerate,
  loading,
  fontOptions,
  theme,
  onImageChange // New callback prop
}) => {
  const classes = getThemeClasses(theme);
  const fileInputRef = useRef(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      // Validate file type
      if (!selectedFile.type.startsWith('image/')) {
        toast('Please upload an image file');
        return;
      }
      
      // Validate file size (e.g., max 5MB)
      if (selectedFile.size > 5 * 1024 * 1024) {
        toast('Image size should be less than 5MB');
        return;
      }

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setUploadedFile(selectedFile);
        
        // Notify parent component about the image change
        if (onImageChange) {
          onImageChange(selectedFile, reader.result);
        }
      };
      reader.readAsDataURL(selectedFile);
    }
  };

  const handleRemoveImage = () => {
    setImagePreview(null);
    setUploadedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    
    // Notify parent that image was removed
    if (onImageChange) {
      onImageChange(null, null);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={`max-w-3xl w-full ${classes.bgPromptBox} backdrop-blur-2xl border ${classes.borderMain} rounded-2xl p-6 shadow-lg`}
    >
      {/* Font Selector */}
      <div className="mb-4">
        <FontSelector font={font} setFont={setFont} fontOptions={fontOptions} theme={theme} />
      </div>

      {/* Image Preview */}
      {imagePreview && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="mb-4"
        >
          <div className="relative inline-block">
            <img
              src={imagePreview}
              alt="Upload preview"
              className={`w-32 h-32 object-cover rounded-xl border-3 ${classes.borderMain} shadow-md`}
            />
            <button
              onClick={handleRemoveImage}
              className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1.5 shadow-lg transition-all"
              title="Remove image"
            >
              <X size={16} />
            </button>
            
          </div>
        </motion.div>
      )}

      {/* Textarea with Action Buttons */}
      <div className="relative">
        <textarea
          value={prompt}
          onChange={(e) => onPromptChange(e.target.value)}
          placeholder="Describe your vision... A futuristic Arabic calligraphy design with golden accents..."
          className={`w-full h-40 rounded-xl p-4 pr-28 ${classes.bgInput} outline-none resize-none text-sm leading-relaxed ${classes.textPlaceholder} focus:ring-2 focus:ring-[#00ff9d]/30 transition-all`}
        />
        
        {/* Action Buttons Container */}
        <div className="absolute bottom-4 right-4 flex gap-2">
          {/* Image Upload Button */}
          <button
            onClick={handleImageClick}
            disabled={loading}
            className={`p-3 rounded-xl ${
              imagePreview 
                ? 'bg-[#00ff9d]/20 border-2 border-[#00ff9d]' 
                : `${classes.bgInput} border ${classes.borderMain}`
            } hover:shadow-[0_0_15px_#00ff9d50] transition-all disabled:opacity-30 disabled:cursor-not-allowed group`}
            title="Upload reference image"
          >
            <ImagePlus 
              className={`${imagePreview ? 'text-[#00ff9d]' : classes.textPlaceholder} group-hover:scale-110 transition-transform`} 
              size={20} 
            />
          </button>

          {/* Generate Button */}
          <button
            onClick={onGenerate}
            disabled={!prompt || loading}
            className="p-3 rounded-xl bg-gradient-to-r from-[#00ff9d] to-[#22e4ac] hover:shadow-[0_0_25px_#00ff9d70] transition-all disabled:opacity-30 disabled:cursor-not-allowed group"
            title={loading ? "Generating..." : "Generate"}
          >
            {loading ? (
              <Loader2 className="text-black animate-spin" size={20} />
            ) : (
              <Send className="text-black group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" size={20} />
            )}
          </button>
        </div>

        {/* Hidden File Input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageChange}
          className="hidden"
        />
      </div>

      {/* Helper Text */}
      <div className="mt-3 flex items-center justify-between">
        <p className={`text-xs ${classes.textPlaceholder}`}>
          {imagePreview 
            ? '✓ Reference image added' 
            : 'Tip: Upload a reference image for better results'}
        </p>
        <p className={`text-xs ${classes.textPlaceholder}`}>
          {prompt.length} characters
        </p>
      </div>
    </motion.div>
  );
};

const ExpandedImageView = ({ image, userId, onClose, theme }) => {
  if (!image) return null;

  const imageUrl = `${config.GLD_IMG_URL}${userId}/${image.subPrompts[image.subPrompts.length - 1].resultImagePath}`;
  const classes = getThemeClasses(theme);

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="w-full max-w-5xl mb-8">
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        className={`relative ${classes.bgImageCard} rounded-2xl shadow-2xl border-2 ${classes.borderMain} border-[#00ff9d] overflow-hidden`}>

        {/* Header Bar with Close Button */}
        <div className={`flex items-center justify-between px-6 py-4 ${classes.bgImagePrompt}`}>
          <h3 className={`text-lg font-semibold ${classes.textImagePrompt}`}>
            Preview
          </h3>
          <button
            onClick={onClose}
            className="bg-black/60 hover:bg-black/80 text-white rounded-full p-2 shadow-lg transition-all"
            title="Close Preview">

            <X size={20} />
          </button>
        </div>

        {/* Expanded Image */}
        <div className={` ${theme === 'dark' ? 'bg-gray-900/30' : 'bg-gray-100/30'}`}>
          <img
            src={imageUrl}
            alt={image.userPrompt || "Generated"}
            className="w-full h-auto max-h-[600px] object-contain mx-auto rounded-lg shadow-lg"/>
        </div>

        {/* Prompt at Bottom 
        <div className={`${classes.bgImagePrompt} border-t ${classes.borderMain} px-6 py-4`}>
          <p className={`text-sm leading-relaxed font-medium ${classes.textImagePrompt}`}>
            <span className="text-[#00ff9d] font-semibold">Prompt:</span> {image.userPrompt}
          </p>
        </div>*/}
      </motion.div>
    </motion.div>
  );
};


const ImageCard = ({ img, index, isSelected, onClick, onDownload, userId, theme }) => {
  const { userPrompt, subPrompts } = img;
  const imageUrl = `${config.GLD_IMG_URL}${userId}/${subPrompts[subPrompts.length - 1].resultImagePath}`;
  const classes = getThemeClasses(theme);
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className="flex flex-col items-center w-full space-y-3">
      
      {/* Image Card */}
      <motion.div
        animate={{
          opacity: 1,
          scale: 1.02 ,
        }}
        transition={{ duration: 0.3 }}
        onClick={onClick}
        className={`relative w-full ${classes.bgImageCard} rounded-2xl shadow-md border ${classes.borderMain} overflow-hidden transition-all hover:shadow-xl hover:border-[#00ff9d]/30 cursor-pointer group`}>

        <button 
          onClick={(e) => {
            e.stopPropagation();
            onDownload(imageUrl);
          }}
          className="absolute top-5 right-5 bg-black/60 hover:bg-black/80 text-[#00ff9d] border border-[#00ff9d]/30 rounded-full p-2 shadow-md transition-all duration-200 z-10"
          title="Download Image">

          <Download size={18} />
        </button>

        <img
          src={imageUrl}
          loading="lazy"
          alt={userPrompt || "Generated"}
          className={`w-full max-h-[420px] object-contain ${theme === 'dark' ? 'bg-gray-900/30' : 'bg-gray-100/30'}`}/>
        
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center">
          <div className="p-3 rounded-full bg-[#00ff9d]/20 text-[#00ff9d] border border-[#00ff9d]/50">
            <SlidersHorizontal size={20} />
          </div>
        </div>

        {isSelected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute inset-0 border-4 border-[#00ff9d] rounded-2xl pointer-events-none"
          />
        )}
      </motion.div>

      {/* Prompt Below Image */}
      <div className={`w-full ${classes.bgImagePrompt} border ${classes.borderMain} rounded-2xl px-4 py-3 shadow-sm flex items-start justify-between gap-3`}>
        <p className={`text-sm leading-relaxed font-medium ${classes.textImagePrompt} flex-1`}>
          {userPrompt}
        </p>
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigator.clipboard.writeText(userPrompt);
            toast("Image prompt copied!");
          }}
          className={`flex-shrink-0 p-1.5 rounded-lg ${classes.textImagePrompt} hover:bg-[#00ff9d]/10 transition-all duration-200`}
          title="Copy prompt">
          <Copy size={16} />
        </button>
      </div>

    </motion.div>
  );
};

const Gallery = ({ images, selectedImageIndex, onImageClick, onDownload, userId, isLoading, theme, expandedImage, onCloseExpanded }) => {
  if (isLoading) {
    return <MiniLoader theme={theme} />;
  }
  if (images.length === 0) {
    return null;
  }
  
  return (
    <div className="mt-8 w-full max-w-5xl flex flex-col items-center">
      {/* Expanded Image View at Top */}
      {expandedImage && (
        <ExpandedImageView
          image={expandedImage}
          userId={userId}
          onClose={onCloseExpanded}
          theme={theme}
        />
      )}

      {/* Gallery Grid Below */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.6 }}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 w-full"
      >
        {images.map((img, i) => (
          <ImageCard
            key={img._id}
            img={img}
            index={i}
            isSelected={selectedImageIndex === i}
            onClick={() => onImageClick(i, img)}
            onDownload={onDownload}
            userId={userId}
            theme={theme}
          />
        ))}
      </motion.div>
    </div>
  );
};

const ArabicPosterCTA = ({ theme }) => {
  const classes = getThemeClasses(theme);
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
      className="text-center max-w-lg"
    >
      <div className="p-4 bg-gradient-to-r from-[#00ff9d]/20 to-[#22e4ac]/20 rounded-full inline-block mb-6 border-2 border-[#00ff9d]/50 shadow-[0_0_30px_#00ff9d30]">
        <Sparkles className="text-[#00ff9d]" size={48} />
      </div>
      <h2 className={`text-3xl font-bold mb-4 ${theme === 'dark' ? 'bg-gradient-to-r from-white to-white/70 bg-clip-text text-transparent' : 'text-gray-900'}`}>
        Generate Posters with Pure Arabic Text
      </h2>
      <p className={`text-lg mb-8 ${classes.textSecondary}`}>
        Select a thread from the sidebar to start generating, or create a new thread. Our engine excels at rendering beautiful, accurate Arabic typography in your images.
      </p>
      <div className="flex justify-center gap-4">
        <p className="text-sm text-[#00ff9d]/70">Select a project and thread to begin.</p>
      </div>
    </motion.div>
  );
};

// Collapsible Section Component
const CollapsibleSection = ({ title, icon, children, defaultOpen = true, theme }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const classes = getThemeClasses(theme);
  
  return (
    <div className={`rounded-xl border ${theme === 'dark' ? 'border-gray-700 bg-gray-800/30' : 'border-gray-300 bg-gray-50/30'} overflow-hidden`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-4 py-3 flex items-center justify-between ${theme === 'dark' ? 'hover:bg-gray-700/50' : 'hover:bg-gray-200/50'} transition-colors`}
      >
        <div className="flex items-center gap-2">
          {icon}
          <span className={`font-semibold text-sm ${classes.textToolbarTitle}`}>{title}</span>
        </div>
        {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>
      
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="p-4 space-y-4">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Enhanced Position Controller with 2D pad
const PositionController = ({ editPosX, setEditPosX, editPosY, setEditPosY, animatedEditPosX, animatedEditPosY, textXPosition, textYPosition, theme }) => {
  const classes = getThemeClasses(theme);
  const [isDragging, setIsDragging] = useState(false);

  const handlePadInteraction = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    setEditPosX(Math.max(0, Math.min(100, x)).toFixed(0));
    setEditPosY(Math.max(0, Math.min(100, y)).toFixed(0));
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    handlePadInteraction(e);
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      handlePadInteraction(e);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  return (
    <div>
      <label className={`text-sm ${classes.textInputLabel} mb-3 block flex items-center gap-2 font-medium`}>
        <Move size={16} /> Text Position
      </label>
      
      {/* 2D Position Pad */}
      <div 
        className={`relative w-full h-48 rounded-xl border-2 ${theme === 'dark' ? 'border-gray-700 bg-gray-800' : 'border-gray-300 bg-gray-100'} cursor-crosshair overflow-hidden mb-3`}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        {/* Grid Background */}
        <div className="absolute inset-0 opacity-20">
          <div className="h-full w-full" style={{
            backgroundImage: `linear-gradient(${theme === 'dark' ? '#ffffff20' : '#00000020'} 1px, transparent 1px), linear-gradient(90deg, ${theme === 'dark' ? '#ffffff20' : '#00000020'} 1px, transparent 1px)`,
            backgroundSize: '20px 20px'
          }} />
        </div>
        
        {/* Center Lines */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className={`absolute w-full h-px ${theme === 'dark' ? 'bg-gray-600' : 'bg-gray-400'}`} />
          <div className={`absolute h-full w-px ${theme === 'dark' ? 'bg-gray-600' : 'bg-gray-400'}`} />
        </div>
        
        {/* Position Indicator */}
        <motion.div
          animate={{
            left: `${editPosX}%`,
            top: `${editPosY}%`,
          }}
          className="absolute w-6 h-6 -ml-3 -mt-3 rounded-full bg-[#00ff9d] border-2 border-white shadow-lg cursor-move"
          style={{ pointerEvents: 'none' }}
        >
          <div className="absolute inset-0 rounded-full bg-[#00ff9d] animate-ping opacity-75" />
        </motion.div>
      </div>

      {/* Fine-tune Sliders */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className={`text-xs ${classes.textInputLabel} mb-2 flex items-center justify-between`}>
            <span>↔️ Horizontal</span>
            <span className="font-mono font-semibold text-[#00ff9d]">{animatedEditPosX}%</span>
          </div>
          <input
            type="range"
            min="0"
            max="100"
            value={editPosX}
            onChange={(e) => setEditPosX(e.target.value)}
            className="w-full accent-[#00ff9d] h-2"
          />
        </div>
        <div>
          <div className={`text-xs ${classes.textInputLabel} mb-2 flex items-center justify-between`}>
            <span>↕️ Vertical</span>
            <span className="font-mono font-semibold text-[#00ff9d]">{animatedEditPosY}%</span>
          </div>
          <input
            type="range"
            min="0"
            max="100"
            value={editPosY}
            onChange={(e) => setEditPosY(e.target.value)}
            className="w-full accent-[#00ff9d] h-2"
          />
        </div>
      </div>

      {/* Pixel Position Display */}
      <div className={`mt-3 text-center text-xs ${classes.textInputLabel} ${classes.bgToolbarInput} rounded-lg py-2 font-mono`}>
        📍 Canvas: {textXPosition} × {textYPosition} px
      </div>
    </div>
  );
};

// Magic Edit Toolbar
const MagicEditToolbar = ({
  selectedImageIndex,
  editFont,
  setEditFont,
  editPosX,
  setEditPosX,
  editPosY,
  setEditPosY,
  editColor,
  setEditColor,
  editFontSize,
  setEditFontSize,
  arabicRenderText,
  setArabicRenderText,
  animatedEditPosX,
  animatedEditPosY,
  animatedFontSize,
  textXPosition,
  textYPosition,
  fontOptions,
  setTextEffect,
  textEffect,
  onToolsRegenerate,
  onClose,
  renderPrompt,
  setRenderPrompt,
  isPromptRendering,
  isToolRendering,
  onPromptRegenerate,
  theme
}) => {
  const [activeTab, setActiveTab] = useState('tools');
  const classes = getThemeClasses(theme);

  // Reusable Apply Button Component for Sections
  const ApplyTrigger = () => (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onToolsRegenerate();
      }}
      disabled={isToolRendering}
      className={`p-1.5 rounded-lg bg-[#00ff9d]/10 hover:bg-[#00ff9d]/20 text-[#00ff9d] transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed`}
      title="Apply Changes"
    >
      {isToolRendering ? (
        <Loader2 className="animate-spin" size={16} />
      ) : (
        <Check size={16} />
      )}
    </button>
  );

  return (
    <motion.aside
      initial={{ x: "100%", opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: "100%", opacity: 0 }}
      transition={{ type: "spring", damping: 25, stiffness: 200 }}
      className={`w-96 ${classes.bgToolbar} backdrop-blur-sm border ${classes.borderMain} shadow-2xl rounded-2xl z-10 flex flex-col overflow-hidden shrink-0`}
    >
      {/* Header */}
      <div className={`flex items-center justify-between px-6 py-5 ${classes.borderToolbarHeader} ${theme === 'dark' ? 'bg-gray-900/30' : 'bg-gray-100/30'}`}>
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-[#00ff9d]/10">
            <Sparkles size={20} className="text-[#00ff9d]" />
          </div>
          <div>
            <h3 className={`text-lg font-semibold ${classes.textToolbarTitle}`}>Magic Edit</h3>
            <p className={`text-xs ${classes.textToolbarSub}`}>Advanced Image Editor</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className={`p-2 rounded-lg hover:bg-white/10 ${theme === 'dark' ? 'text-white/60 hover:text-white' : 'text-gray-600 hover:text-gray-900'} transition`}
        >
          <X size={20} />
        </button>
      </div>

      {/* Tab Buttons */}
      <div className="px-6 pt-5">
        <div className={`flex p-1 ${classes.bgToolbarTab} rounded-lg border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-300'}`}>
          <button
            onClick={() => setActiveTab('tools')}
            className={`flex-1 p-2.5 rounded-md ${
              activeTab === 'tools'
                ? 'bg-[#00ff9d] text-black shadow-lg'
                : theme === 'dark' ? 'text-white/60 hover:bg-gray-700/50' : 'text-gray-600 hover:bg-gray-300/50'
            } transition-all text-sm font-semibold`}
          >
            🛠️ Tools
          </button>
          <button
            onClick={() => setActiveTab('prompt')}
            className={`flex-1 p-2.5 rounded-md ${
              activeTab === 'prompt'
                ? 'bg-[#00ff9d] text-black shadow-lg'
                : theme === 'dark' ? 'text-white/60 hover:bg-gray-700/50' : 'text-gray-600 hover:bg-gray-300/50'
            } transition-all text-sm font-semibold`}
          >
            ✨ AI Prompt
          </button>
        </div>
      </div>

      {/* Controls */}
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
        <AnimatePresence mode="wait">
          {activeTab === 'tools' && (
            <motion.div
              key="tools"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-4"
            >
              {/* Text Content Section */}
              <CollapsibleSection title="Text Content" icon={<Type size={18} className="text-[#00ff9d]" />} theme={theme}>
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className={`text-xs ${classes.textInputLabel} font-medium`}>
                      Arabic Text
                    </label>
                    <ApplyTrigger />
                  </div>
                  <textarea
                    value={arabicRenderText}
                    onChange={(e) => setArabicRenderText(e.target.value)}
                    placeholder="Enter your Arabic text here..."
                    className={`w-full h-24 ${classes.bgInput} rounded-lg p-3 outline-none resize-none text-sm ${classes.textPlaceholder} focus:ring-2 focus:ring-[#00ff9d]/50 transition`}
                  />
                </div>
              </CollapsibleSection>

              {/* Typography Section */}
              <CollapsibleSection title="Typography" icon={<Type size={18} className="text-[#00ff9d]" />} theme={theme}>
                <div className="relative">
                  <div className="flex justify-end mb-2">
                    <ApplyTrigger />
                  </div>
                  <ToolbarFontSelector 
                    editFont={editFont}
                    setEditFont={setEditFont}
                    fontOptions={fontOptions}
                    theme={theme}
                  />
                  
                  <FontSizeController
                    value={editFontSize}
                    onChange={setEditFontSize}
                    animatedValue={animatedFontSize}
                    theme={theme}
                  />
                </div>
              </CollapsibleSection>

              {/* Position & Layout Section */}
              <CollapsibleSection title="Position & Layout" icon={<Move size={18} className="text-[#00ff9d]" />} theme={theme}>
                <div className="relative">
                   <div className="flex justify-end mb-2">
                    <ApplyTrigger />
                  </div>
                  <PositionController
                    editPosX={editPosX}
                    setEditPosX={setEditPosX}
                    editPosY={editPosY}
                    setEditPosY={setEditPosY}
                    animatedEditPosX={animatedEditPosX}
                    animatedEditPosY={animatedEditPosY}
                    textXPosition={textXPosition}
                    textYPosition={textYPosition}
                    theme={theme}
                  />
                </div>
              </CollapsibleSection>

              {/* Colors Section */}
              <CollapsibleSection title="Colors" icon={<Palette size={18} className="text-[#00ff9d]" />} theme={theme}>
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className={`text-xs ${classes.textInputLabel} font-medium`}>
                      Text Color
                    </label>
                    <ApplyTrigger />
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={editColor}
                      onChange={(e) => setEditColor(e.target.value)}
                      className={`w-16 h-16 rounded-lg border-2 ${theme === 'dark' ? 'border-gray-700' : 'border-gray-300'} cursor-pointer hover:border-[#00ff9d]/50 transition`}
                    />
                    <div className="flex-1">
                      <div className={`text-sm ${theme === 'dark' ? 'text-white/90' : 'text-gray-800'} font-mono ${classes.bgToolbarInput} rounded-lg px-4 py-3 text-center font-semibold`}>
                        {editColor.toUpperCase()}
                      </div>
                      <div className="mt-2 flex gap-2">
                        {['#FFFFFF', '#000000', '#00ff9d', '#FF6B6B'].map((color) => (
                          <button
                            key={color}
                            onClick={() => setEditColor(color)}
                            className="w-8 h-8 rounded-md border-2 border-gray-600 hover:scale-110 transition-transform"
                            style={{ backgroundColor: color }}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </CollapsibleSection>

              {/* Text Effects Section */}
              <CollapsibleSection title="Text Effects" icon={<Sparkles size={18} className="text-[#00ff9d]" />} theme={theme} defaultOpen={false}>
                <div className="relative">
                   <div className="flex justify-end mb-2">
                    <ApplyTrigger />
                  </div>
                  <TextEffectsSection 
                    theme={theme}
                    setTextEffect={setTextEffect}
                    textEffect={textEffect} />
                </div>
              </CollapsibleSection>
            </motion.div>
          )}

          {activeTab === 'prompt' && (
            <motion.div
              key="prompt"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-4"
            >
              <div className={`${classes.bgToolbarInput} rounded-xl p-4 border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-300'}`}>
                <div className="flex items-start gap-3 mb-3">
                  <Sparkles size={20} className="text-[#00ff9d] mt-1" />
                  <div>
                    <h4 className={`font-semibold ${classes.textToolbarTitle} mb-1`}>AI-Powered Editing</h4>
                    <p className={`text-xs ${classes.textToolbarSub}`}>
                      Describe how you want to edit your image using natural language
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className={`text-sm ${classes.textInputLabel} mb-3 block font-medium`}>
                  Edit Instructions
                </label>
                <textarea
                  value={renderPrompt}
                  onChange={(e) => setRenderPrompt(e.target.value)}
                  placeholder="e.g., 'Center the text and make it 48px, change color to white with a subtle shadow'"
                  className={`w-full h-40 ${classes.bgInput} rounded-xl p-4 outline-none resize-none text-sm ${classes.textPlaceholder} focus:ring-2 focus:ring-[#00ff9d]/50 transition`}
                />
                <div className={`mt-2 text-xs ${classes.textToolbarSub} flex items-center gap-1`}>
                  💡 <span>Be specific for best results</span>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Actions - Only shown for Prompt tab now */}
      {activeTab === 'prompt' && (
        <div className={`px-6 py-5 ${classes.borderToolbarFooter} ${classes.bgToolbarAction} space-y-3`}>
          <button
            onClick={onPromptRegenerate}
            disabled={isPromptRendering || !renderPrompt.trim()}
            className="w-full flex items-center justify-center gap-2 px-4 py-4 rounded-xl bg-gradient-to-r from-[#00ff9d] to-[#22e4ac] text-black font-bold hover:shadow-[0_0_30px_#00ff9d70] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPromptRendering ? (
              <>
                <Loader2 className="animate-spin" size={18} /> Applying Magic...
              </>
            ) : (
              <>
                <Sparkles size={18} /> Apply AI Magic
              </>
            )}
          </button>
        </div>
      )}
    </motion.aside>
  );
};

// Text Effects Section
const TextEffectsSection = ({ theme, setTextEffect, textEffect }) => {
  const classes = getThemeClasses(theme);

  const effects = [
    { id: "none", label: "None", icon: CircleDot },
    { id: "wood", label: "Wood", icon: Trees },
    { id: "fire", label: "Fire Flames", icon: Flame },
    { id: "neon", label: "Neon Light", icon: Sparkles },
  ];

  return (
    <div className="grid grid-cols-3 gap-2">
      {effects.map((effect) => {
        const isActive = textEffect === effect.id;
        const Icon = effect.icon;

        return (
          <motion.button
            key={effect.id}
            onClick={() => {setTextEffect(effect.id); }}
            whileHover={{ scale: 1.06 }}
            whileTap={{ scale: 0.95 }}
            animate={{
              scale: isActive ? 1.08 : 1,
              boxShadow: isActive ? "0px 0px 12px rgba(0,255,157,0.6)" : "none",
            }}
            transition={{ type: "spring", stiffness: 260, damping: 18 }}
            className={`p-3 rounded-xl text-center border transition-all ${
              isActive
                ? "bg-[#00ff9d] text-black font-semibold border-transparent"
                : theme === "dark"
                ? "bg-gray-800 border-gray-700 text-white/70 hover:bg-gray-700"
                : "bg-gray-200 border-gray-300 text-gray-700 hover:bg-gray-300"
            }`}
          >
            <Icon className="mx-auto mb-1" size={22} strokeWidth={2} />
            <div className="text-xs font-semibold">{effect.label}</div>
          </motion.button>
        );
      })}
    </div>
  );
};



const EditControlSlider = ({ label, icon, value, onChange, animatedValue, theme }) => {
  const classes = getThemeClasses(theme);
  return (
    <div>
      <label className={`text-sm ${classes.textInputLabel} mb-3 block flex items-center gap-2 font-medium`}>
        <span>{icon}</span> {label}
      </label>
      <input
        type="range"
        min="0"
        max="100"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full accent-[#00ff9d] h-2"
      />
      <motion.div
        key={animatedValue}
        initial={{ opacity: 0, y: -3 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className={`text-sm ${classes.textToolbarRangeIndicator} mt-2 font-semibold text-center rounded-lg py-2 border ${classes.bgToolbarRangeIndicator}`}
      >
        {animatedValue}%
      </motion.div>
    </div>
  );
};

// Enhanced Font Size Controller
const FontSizeController = ({ label, icon, value, onChange, animatedValue, theme }) => {
  const classes = getThemeClasses(theme);
  
  const presetSizes = [16, 20, 24, 32, 48, 64, 96, 128];
  
  return (
    <div>
      <label className={`text-sm ${classes.textInputLabel} mb-3 block flex items-center justify-between font-medium`}>
        <span className="flex items-center gap-2">
          <span>{icon}</span> {label}
        </span>
        <motion.span
          key={animatedValue}
          initial={{ scale: 1.2, color: '#00ff9d' }}
          animate={{ scale: 1, color: theme === 'dark' ? '#ffffff' : '#000000' }}
          className="font-mono font-bold text-[#00ff9d]"
        >
          {animatedValue}
        </motion.span>
      </label>
      
      {/* Slider */}
      <input
        type="range"
        min="15"
        max="200"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full accent-[#00ff9d] h-2 mb-3"
      />
      
      {/* Animated Value Display */}
      <motion.div
        key={animatedValue}
        initial={{ opacity: 0, y: -3 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className={`text-sm ${classes.textToolbarRangeIndicator} mb-3 font-semibold text-center rounded-lg py-2 border ${classes.bgToolbarRangeIndicator}`}
      >
        {animatedValue}
      </motion.div>
      
      {/* Preset Buttons */}
      <div className="flex gap-2 flex-wrap">
        {presetSizes.map((size) => (
          <button
            key={size}
            onClick={() => onChange(size)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              parseInt(value) === size
                ? 'bg-[#00ff9d] text-black shadow-md'
                : theme === 'dark'
                ? 'bg-gray-800 text-white/70 hover:bg-gray-700'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {size}
          </button>
        ))}
      </div>
    </div>
  );
};

const Modal = ({ show, title, onClose, children, theme }) => {
  const classes = getThemeClasses(theme);
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className={`${classes.bgModal} rounded-2xl p-6 w-full max-w-md`}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-xl font-semibold ${classes.textBase}`}>{title}</h3>
              <button
                onClick={onClose}
                className={`p-2 rounded-lg hover:bg-white/10 ${theme === 'dark' ? 'text-white/60 hover:text-white' : 'text-gray-600 hover:text-gray-900'} transition`}
              >
                <X size={20} />
              </button>
            </div>
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const CreateForm = ({ name, setName, description, setDescription, onSubmit, submitLabel, theme }) => {
  const classes = getThemeClasses(theme);
  return (
    <div className="space-y-4">
      <div>
        <label className={`text-sm ${classes.textInputLabel} mb-2 block`}>Name</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Enter name..."
          className={`w-full ${classes.bgInput} rounded-xl p-3 outline-none`}
        />
      </div>

      <div>
        <label className={`text-sm ${classes.textInputLabel} mb-2 block`}>Description (Optional)</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Add a description..."
          className={`w-full h-24 ${classes.bgInput} rounded-xl p-3 outline-none resize-none`}
        />
      </div>

      <button
        onClick={onSubmit}
        disabled={!name.trim()}
        className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-[#00ff9d] to-[#22e4ac] text-black font-semibold hover:shadow-[0_0_25px_#00ff9d70] transition-all disabled:opacity-50"
      >
        <Sparkles size={18} />
        {submitLabel}
      </button>
    </div>
  );
};

// Main Component
export default function AlienesiaStudioMerged() {
  // Mock auth token and userId (replace with actual auth)
  const token = Cookies.get('authToken');

  // --- Theme State with Cookie Persistence ---
  const [theme, setTheme] = useState(() => {
    // Initialize theme from cookie or default to 'dark'
    const savedTheme = Cookies.get('userTheme');
    return savedTheme || 'dark';
  });

  const toggleTheme = useCallback(() => {
    setTheme(prevTheme => {
      const newTheme = prevTheme === 'dark' ? 'light' : 'dark';
      // Save to cookie with 365 days expiration
      Cookies.set('userTheme', newTheme, { expires: 365 });
      return newTheme;
    });
  }, []);

  const classes = getThemeClasses(theme);

  // -------------------

  // State
  const [prompt, setPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [images, setImages] = useState([]);

  const [editPosX, setEditPosX] = useState(50);
  const [editPosY, setEditPosY] = useState(50);

  const [xPosition, setXPosition] = useState();
  const [yPosition, setYPosition] = useState();

  const [editFontSize, setEditFontSize] = useState(0);
  const [arabicRenderText, setArabicRenderText] = useState("");
  
  const [editColor, setEditColor] = useState("#00ff9d");

  // Fonts Initiations 
  const [editFont, setEditFont] = useState("Amin");
  const [selectedFont, setSelectedFont] = useState("ArghavanBold");

  const [imageHeightSize, setImageHeightSize] = useState(0);
  const [imageWidthSize, setImageWidthSize] = useState(0);

  // Animated values
  let animatedEditPosX = useAnimatedValue(editPosX);
  let animatedEditPosY = useAnimatedValue(editPosY);
  const animatedFontSize = useAnimatedValue(editFontSize);

  // Projects and Threads
  const [projects, setProjects] = useState([]);
  const [threads, setThreads] = useState([]);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showThreadModal, setShowThreadModal] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [newProjectDesc, setNewProjectDesc] = useState("");
  const [newThreadName, setNewThreadName] = useState("");
  const [newThreadDesc, setNewThreadDesc] = useState("");
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [selectedThreadId, setSelectedThreadId] = useState(null);

  // Loading states
  const [isProjectsLoading, setIsProjectsLoading] = useState(false);
  const [isThreadsLoading, setIsThreadsLoading] = useState(false);
  const [isImageRecordsLoading, setIsImageRecordsLoading] = useState(false);

  const [textEffect, setTextEffect] = useState('none');

  const fontsMap = {
    ArghavanBold: {
      name: "النيل الأبيض",
      path: "/fonts/ArghavanBold.ttf"
    },
    ArslanWessam: {
      name: "كسلا",
      path: "/fonts/ArslanWessam.ttf"
    },
    Amin: {
      name: "الخرطوم",
      path: "/fonts/AminRegular.ttf"
    },
    Andalus: {
      name: "عطبرة",
      path: "/fonts/AndalusArabicFont.ttf"
    },
    NotoNaskhArabic: {
      name: "الأبيض",
      path: "/fonts/NotoNaskhArabic-Regular.ttf"
    },
    NotoKufiArabicBold: {
      name: "بورتسودان",
      path: "/fonts/NotoKufiArabic-Bold.ttf"
    },
    DejaVuSans: {
      name: "الشمالية",
      path: "/fonts/DejaVuSans.ttf"
    },
    NotoSansArabic: {
      name: "الجزيرة",
      path: "/fonts/NotoSansArabic.ttf"
    },
    JannaLTBold: {
      name: "النيل الأبيض",
      path: "/fonts/JannaLTBold.ttf"
    },
    KhebratMusamimR: {
      name: "نهر النيل",
      path: "/fonts/KhebratMusamimR.ttf"
    },
    AFSANEH: {
      name: "القضارف",
      path: "/fonts/AFSANEH.ttf"
    },
    Rakkas: {
      name: "بحري",
      path: "/fonts/Rakkas-Regular.ttf"
    },
    Hoba: {
      name: "تجاني",
      path: "/fonts/Hoba.ttf"
    },
    Shareb: {
      name: "تجاني",
      path: "/fonts/Shareb.ttf"
    }  
  };

  // Edit controls
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageRecordId, setSelectedImageRecordId] = useState(null);
  const [subPromptId, setSubPromptId] = useState(null);
  const [renderPrompt, setRenderPrompt] = useState("");
  const [renderImageUrl, setRenderImageUrl] = useState("");
  const [isRendering, setIsRendering] = useState(false);
  const [renderingByTool, setRenderingByTool] = useState(false);

  const [userId, setTempUserId] = useState();

  const [expandedImage, setExpandedImage] = useState(null);

  // Add these states in your parent component
  const [uploadedImage, setUploadedImage] = useState(null);
  const [uploadedImagePreview, setUploadedImagePreview] = useState(null);


  // Load projects on mount
  useEffect(() => {
    setTempUserId(Cookies.get('userId'));
    loadProjects();
  }, []);

  // Handler for image upload
  const handleImageUpload = (file, preview) => {
    setUploadedImage(file);
    setUploadedImagePreview(preview);
  };


  const parseDimensions = (sizeString) => {
    // Assuming format is like "1024x768" or similar
    const [width, height] = sizeString.split('x').map(num => parseInt(num, 10));
    
    // Update state
    setImageWidthSize(width);
    setImageHeightSize(height);
    
    // Return values for immediate use
    return { width, height };
  };

  // API Integration Functions
  const loadProjects = async () => {
    setIsProjectsLoading(true);
    try {
      const _projects = await getProjects(token);
      if (_projects.length > 0) {
        setProjects(_projects);
        setSelectedProjectId(_projects[0]._id);
        loadProjectThreads(_projects[0]._id);
      }
    } catch (error) {
      toast("Error loading projects");
    } finally {
      setIsProjectsLoading(false);
    }
  };

  const loadProjectThreads = async (projectId) => {
    setSelectedProjectId(projectId);
    setIsThreadsLoading(true);
    setSelectedThreadId(null);
    setImages([]);

    try {
      const _threads = await getThreads(projectId, token);
      if (_threads) {
        setThreads(_threads);
      }
    } catch (error) {
      toast("Error loading threads");
    } finally {
      setIsThreadsLoading(false);
    }
  };

  const loadThreadRecords = async (threadId) => {
    setIsImageRecordsLoading(true);
    setSelectedThreadId(threadId);
    try {
      const imageRecords = await getThreadRecords(threadId, token);
      if (imageRecords) {
        setImages(imageRecords);
        return imageRecords; // Add this line to return the fresh data
      }
      return []; // Return empty array if no records
    } catch (error) {
      toast("Network error, try again.");
      return []; // Return empty array on error
    } finally {
      setIsImageRecordsLoading(false);
    }
  };

  //Generate New Poster by Calling makePoster
  const handleGenerate = async () => {

    if(uploadedImage != null){
      imageToImageGeneration();
    } 

    if(!uploadedImage){
      textToImageGeneration();
    }
  };

  //Text to Image (Poster)
  const textToImageGeneration = async() => {
    if (!prompt.trim() || !selectedThreadId) return;

    setIsGenerating(true);
    try {
      const response = await makePoster(prompt, selectedThreadId, selectedFont, token);
      await loadThreadRecords(selectedThreadId);
      setPrompt("");
      toast("Poster generated successfully!");
    } catch (error) {
      toast("Error generating poster");
    } finally {
      setIsGenerating(false);
    }
  }

  const imageToImageGeneration = async() => {
    if (!prompt.trim() || !selectedThreadId) return;

    setIsGenerating(true);
    try {
      const response = await makePosterWithImage(prompt, selectedThreadId, selectedFont, token, uploadedImage);

      await loadThreadRecords(selectedThreadId);
      setPrompt("");
      toast("Poster generated successfully!");
    } catch (error) {
      toast("Error generating poster");
    } finally {
      setIsGenerating(false);
    }
  }

  const setActualXPosition = (xPercent) => {
    setEditPosX(xPercent);
    const xPos = Math.round(imageWidthSize * (xPercent / 100));
    setXPosition(xPos);
  }

  const setActualYPosition = (yPercent) => {
    setEditPosY(yPercent);
    const yPos = Math.round(imageHeightSize * (yPercent / 100));
    setYPosition(yPos);
  }

  const handleCloseExpanded = () => {
    setExpandedImage(null);

    setSelectedImageIndex(null);
    setSelectedImage(null);
  };

  const handleImageEdit = async (index, img) => {
    // Show expanded view

    console.log(img);
    console.log(" <<<<>>>>>");

    setExpandedImage(img);
    
    if (selectedImageIndex === index) {
      setSelectedImageIndex(null);
      setSelectedImage(null);
    } else {
      setSelectedImageIndex(index);
      setSelectedImage(img);
      setSelectedImageRecordId(img._id);
      
      const _subPromptId = img.subPrompts[img.subPrompts.length - 1]._id;
      setSubPromptId(_subPromptId);
      //console.log(_subPromptId);
      
      // Set Last Image's Prompt Text, Font and Size
      setArabicRenderText(img.subPrompts[img.subPrompts.length - 1].options.imageText);
      setEditFont(img.subPrompts[img.subPrompts.length - 1].options.fontType);
      setSelectedFont(img.subPrompts[img.subPrompts.length - 1].options.fontType);
      setEditFontSize(img.subPrompts[img.subPrompts.length - 1].options.fontSize);
      setEditColor(img.subPrompts[img.subPrompts.length - 1].options.fontColor);

      //alert(img.subPrompts[img.subPrompts.length - 1].options.fontType);

      // Get dimensions first and store in local variables
      let currentWidth, currentHeight;
      
      // Image URL 
      const imageUrl = `${config.GLD_IMG_URL}${userId}/${img.subPrompts[img.subPrompts.length - 1].resultImagePath}`;
      if (img.imageSize == "auto") {
        const { width, height } = await getImageDimensions(imageUrl);
        setImageWidthSize(parseInt(width, 10));
        setImageHeightSize(parseInt(height, 10));      
      } else {
        const dimensions = parseDimensions(img.imageSize);
        currentWidth = dimensions.width;
        currentHeight = dimensions.height;
        setImageWidthSize(currentWidth);
        setImageHeightSize(currentHeight);
      }
      
      // Get the Pos% of Image's Text and Reflect on the Slider
      let _textXPos = img.subPrompts[img.subPrompts.length - 1].options.position.x;
      let _textYPos = img.subPrompts[img.subPrompts.length - 1].options.position.y;
      setXPosition(_textXPos);
      setYPosition(_textYPos);
      
      // Calculate percentage using the fresh dimensions (not state)
      _textXPos = Math.round((_textXPos / currentWidth) * 100);
      _textYPos = Math.round((_textYPos / currentHeight) * 100);
      
      setEditPosX(_textXPos);
      setEditPosY(_textYPos);
      animatedEditPosX = _textXPos;
      animatedEditPosY = _textYPos;
      
      setRenderImageUrl(imageUrl);
      setRenderPrompt("");
    }
  };

  async function getImageDimensions(imagePath) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        resolve({
          width: img.width,
          height: img.height
        });
      };
      
      img.onerror = () => {
        reject(new Error(`Failed to load image: ${imagePath}`));
      };
      
      img.src = imagePath;
    });
  }

  const handleRegenerate = async () => {
    if (!renderPrompt.trim()) return;

    setIsRendering(true);
    try {
      const rendered_image = await renderPoster(
        selectedImageRecordId, 
        subPromptId, 
        renderPrompt, 
        selectedFont, 
        token
      );

      if (rendered_image) {
        setSubPromptId(rendered_image.newSubPrompt);
        setRenderImageUrl(`${config.GLD_IMG_URL}${userId}/${rendered_image.newImage}`);
        await loadThreadRecords(selectedThreadId);
        toast("Design updated successfully!");
        setExpandedImage(images[selectedImageIndex]);
      }
    } catch (error) {
      toast("Network issue, try again!");
    } finally {
      setIsRendering(false);
    }
  };

  const handleDirectRender = async () => {
    setRenderingByTool(true);
    try {
      const rendered_image = await directRender(
        selectedImageRecordId, 
        subPromptId, 
        arabicRenderText, 
        editFontSize, 
        editColor.toUpperCase(),
        xPosition,
        yPosition,
        selectedFont,
        textEffect,
        token
      );

      if (rendered_image) {
        setSubPromptId(rendered_image.newSubPrompt);
        setRenderImageUrl(`${config.GLD_IMG_URL}${userId}/${rendered_image.newImage}`);
        await loadThreadRecords(selectedThreadId);

        const freshImages = await loadThreadRecords(selectedThreadId);
      
        // Update expandedImage with the fresh data
        if (freshImages && freshImages[selectedImageIndex]) {
          setExpandedImage(freshImages[0]);
        }

        toast("Design updated successfully!");

        setSelectedImageIndex(0);
        
      }

    } catch (error) {
      toast("Network issue, try again!");
    } finally {
      setRenderingByTool(false);
    }
  }

  const handleDownload = (url) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = url.split("/").pop();
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleCreateProject = async () => {
    if (!newProjectName.trim()) return;

    try {
      const _project = await addProject(newProjectName, newProjectDesc, token);
      const newProject = _project.project;

      if (newProject) {
        setProjects([...projects, newProject]);
        setSelectedProjectId(newProject._id);
        setNewProjectName("");
        setNewProjectDesc("");
        toast("Project created!");
      }
    } catch (error) {
      toast("Error creating project");
    }
    setShowProjectModal(false);
  };

  const handleCreateThread = async () => {
    if (!newThreadName.trim()) {
      toast("Thread title is required!");
      return;
    }

    try {
      const _newThread = await addThread(
        selectedProjectId, 
        newThreadName, 
        newThreadDesc, 
        token
      );

      if (_newThread.thread) {
        setThreads([...threads, _newThread.thread]);
        await loadProjectThreads(selectedProjectId);
        setNewThreadName("");
        setNewThreadDesc("");
        toast("Thread created!");
      }
    } catch (error) {
      toast("Error creating thread");
    }
    setShowThreadModal(false);
  };

  const handleProjectSelect = (id) => {
    if (id === selectedProjectId) {
      setSelectedProjectId(null);
      setSelectedThreadId(null);
      setThreads([]);
      setImages([]);
    } else {
      loadProjectThreads(id);
    }
  };

  const handleThreadSelect = (id) => {
    if (id === selectedThreadId) {
      setSelectedThreadId(null);
      setImages([]);
    } else {
      loadThreadRecords(id);
      handleCloseExpanded();
    }
  };

  const selectedProject = projects.find(p => p._id === selectedProjectId);
  const selectedThread = threads.find(t => t._id === selectedThreadId);

  return (
    <div className={`min-h-screen flex flex-col overflow-hidden ${classes.bgScreen}`}>
      <Header theme={theme} toggleTheme={toggleTheme} />

      <ToastContainer
        position="top-left"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={theme === 'dark' ? "dark" : "light"}/>

      <div className="flex flex-1 relative p-6 gap-6 overflow-hidden">
        <Sidebar
          projects={projects}
          threads={threads.filter(t => t.projectId === selectedProjectId)}
          onProjectAdd={() => setShowProjectModal(true)}
          onThreadAdd={() => setShowThreadModal(true)}
          selectedProjectId={selectedProjectId}
          onProjectSelect={handleProjectSelect}
          selectedThreadId={selectedThreadId}
          onThreadSelect={handleThreadSelect}
          isProjectsLoading={isProjectsLoading}
          isThreadsLoading={isThreadsLoading}
          theme={theme}
        />

        <main className="flex-1 flex gap-6 overflow-hidden">
          {/* Main Content Area */}
          <div className={`flex-1 flex flex-col p-6 sm:p-10 ${classes.bgMain} backdrop-blur-sm border ${classes.borderMain} rounded-2xl shadow-2xl relative overflow-y-auto`}>

            {selectedProject && selectedThread && (
              <div className="absolute top-6 left-6 z-10">
                <div 
                  className={`flex items-center space-x-2 px-4 py-2 bg-gray-800/80 backdrop-blur-md border ${theme === 'dark' ? 'border-white/20' : 'border-gray-400'} rounded-lg shadow-xl`}
                >
                  <p className="text-sm font-medium text-white/50">{selectedProject.title}</p>
                  <span className="text-sm text-[#00ff9d]">&gt;</span>
                  <p className="text-sm font-semibold text-white">{selectedThread.title}</p>
                </div>
              </div>
            )}

            {selectedThreadId !== null ? (
              <div className={`flex flex-col flex-1 w-full ${images.length > 0 ? 'justify-between' : 'justify-center items-center'}`}>
                
                {/* Welcome Message when no images */}
                {images.length === 0 && !isImageRecordsLoading && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center py-16 max-w-2xl">

                    <div className="inline-block p-4 bg-gradient-to-br from-[#00ff9d]/20 to-[#22e4ac]/20 rounded-full mb-4 border-2 border-[#00ff9d]/50">
                      <Sparkles className="text-[#00ff9d]" size={48} />
                    </div>
                    <h2 className={`text-3xl font-bold mb-2 ${classes.textWelcome}`}>Create Beautiful Arabic Designs</h2>
                    <p className={classes.textWelcomeSub}>Enter your prompt below to generate stunning Arabic calligraphy and designs</p>
                  </motion.div>
                )}

                {/* Gallery */}
                {images.length > 0 && (
                  <Gallery 
                    images={images} 
                    selectedImageIndex={selectedImageIndex}
                    onImageClick={handleImageEdit}
                    onDownload={handleDownload}
                    userId={userId}
                    isLoading={isImageRecordsLoading}
                    theme={theme}
                    expandedImage={expandedImage}
                    onCloseExpanded={handleCloseExpanded}
                  />
                )}
                
                {/* Loading state */}
                {isImageRecordsLoading && <MiniLoader theme={theme} />}
                
                {/* Prompt Box */}
                {!isImageRecordsLoading && (
                  <div className={`w-full flex justify-center ${images.length > 0 ? 'mt-10' : ''}`}>
                    {/* <PromptBox
                      prompt={prompt}
                      onPromptChange={setPrompt}
                      font={selectedFont}
                      setFont={setSelectedFont}
                      onGenerate={handleGenerate}
                      loading={isGenerating}
                      fontOptions={fontsMap}
                      theme={theme}
                    /> */}

                    <PromptBox
                      prompt={prompt}
                      onPromptChange={setPrompt}
                      font={selectedFont}
                      setFont={setSelectedFont}
                      onGenerate={handleGenerate}
                      loading={isGenerating}
                      fontOptions={fontsMap}
                      theme={theme} 
                      onImageChange={handleImageUpload} 
                    />

                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-1 justify-center items-center">
                <ArabicPosterCTA theme={theme} />
              </div>
            )}
          </div>

          {/* Magic Edit Toolbar */}
          <AnimatePresence>
            {selectedImageIndex !== null && selectedImage && (
              <MagicEditToolbar
                selectedImage={selectedImage}
                renderPrompt={renderPrompt}
                setRenderPrompt={setRenderPrompt}
                editFont={selectedFont}
                setEditFont={setSelectedFont}
                editPosX={editPosX}
                setEditPosX={setActualXPosition}
                editPosY={editPosY}
                setEditPosY={setActualYPosition}
                editColor={editColor}
                setEditColor={setEditColor}
                editFontSize={editFontSize}
                setEditFontSize={setEditFontSize}
                arabicRenderText={arabicRenderText}
                setArabicRenderText={setArabicRenderText}
                animatedEditPosX={animatedEditPosX}
                animatedEditPosY={animatedEditPosY}
                animatedFontSize={animatedFontSize}
                textXPosition={xPosition}
                textYPosition={yPosition}
                fontOptions={fontsMap}
                setTextEffect={setTextEffect}
                textEffect={textEffect}
                onClose={() => {
                  setSelectedImageIndex(null);
                  setSelectedImage(null);
                  //setExpandedImage(null); // Close Expanded Image
                }}
                isRendering={isRendering}
                renderImageUrl={renderImageUrl}
                onPromptRegenerate={handleRegenerate}
                onToolsRegenerate={handleDirectRender}
                isPromptRendering={isRendering}
                isToolRendering={renderingByTool}
                theme={theme}
              />
            )}
          </AnimatePresence>
        </main>

        {/* Modals */}
        <Modal show={showProjectModal} title="Create New Project" onClose={() => setShowProjectModal(false)} theme={theme}>
          <CreateForm
            name={newProjectName}
            setName={setNewProjectName}
            description={newProjectDesc}
            setDescription={setNewProjectDesc}
            onSubmit={handleCreateProject}
            submitLabel="Create Project"
            theme={theme}
          />
        </Modal>

        <Modal show={showThreadModal} title="Create New Thread" onClose={() => setShowThreadModal(false)} theme={theme}>
          <CreateForm
            name={newThreadName}
            setName={setNewThreadName}
            description={newThreadDesc}
            setDescription={setNewThreadDesc}
            onSubmit={handleCreateThread}
            submitLabel="Create Thread"
            theme={theme}
          />
        </Modal>
      </div>
    </div>
  );
}